import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Form,
  Button,
  Modal,
  Header,
  Icon,
  Message,
  Select,
} from 'semantic-ui-react';
import { graphql } from 'react-apollo';
import { isEmpty, get, filter } from 'lodash';
import Validator from 'validatorjs';
import client from '../apollo';

import Search from '../views/Shared/Search';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import ReturnInvoicesList from '../views/CanceledReturnInvoices/List';
import ReturnInvoiceAudit from '../views/ReturnInvoices/Audit';
import auctionLocationsQuery from '../graphql/auctionLocationsQuery';
import returnInvoicesQuery from '../graphql/canceledReturnInvoicesQuery';
import reinstateReturnInvoiceMutation from '../graphql/reinstateReturnInvoiceMutation';
import { setSearch } from '../data/searchField/actions';
import APP_PATH from '../constants/paths';
import { setPage } from '../data/page/actions';

const CanceledReturnInvoices = props => {
  const {
    history,
    reinstateMutation,
    data: { canceledReturnInvoices, refetch },
    setPage: setPageAction,
    setSearchFields: setSearchFieldsAction,
    searchFields,
  } = props;

  const [selectedReturnInvoice, setSelectedReturnInvoice] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [returnLocation, setReturnLocation] = useState('');
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    client
      .query({
        query: auctionLocationsQuery,
        variables: {
          sortBy: 'initials',
        },
      })
      .then(({ data }) => {
        const auctionLocations = get(data, 'locations');
        setLocations(filter(auctionLocations, val => val.acceptsReturns));
      })
      .catch(() => {
        setLocations([]);
      });
  }, []);

  const onSelectReturnInvoice = useCallback((action, returnInvoice) => {
    setSelectedReturnInvoice(returnInvoice);

    switch (action) {
      case 'Edit':
        break;
      case 'Status History':
        setIsAuditModalOpen(true);
        break;
      case 'Reinstate Return':
        setIsConfirmationOpen(true);
        setReturnLocation('');
        setErrors({});
        break;
      case 'Vehicle Status Tracker':
        history.push(
          APP_PATH.VEHICLE_STATUS_TRACKER.replace(
            ':purchaseId',
            returnInvoice.guaranteePurchaseId
          )
        );
        break;
      default:
        setSelectedReturnInvoice(null);
    }
  }, [history]);

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedReturnInvoice(null);
  }, []);

  const handleConfirmationClose = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const reinstateReturnInvoice = useCallback(() => {
    const input = {
      returnInvoiceId: selectedReturnInvoice.id,
      returnLocationId: returnLocation,
    };

    const validationRules = {
      returnLocation: 'required',
    };

    const validation = new Validator({ returnLocation }, validationRules);
    if (validation.fails()) {
      setErrors({ ...validation.errors });
      return;
    }
    setIsConfirmationOpen(false);

    reinstateMutation({
      variables: { input },
    })
      .then(() => {
        refetch();
        setSelectedReturnInvoice(null);
        setFlashMessage('Successfully Reinstated the VRA');
        setPageAction({});
      })
      .catch(() => {
        setFlashMessage('Error while reinstating return invoice');
      });
  }, [
    reinstateMutation,
    selectedReturnInvoice,
    returnLocation,
    refetch,
    setPageAction,
  ]);

  const handleConfirm = useCallback(() => {
    reinstateReturnInvoice();
  }, [reinstateReturnInvoice]);

  const onSearchSubmitHandler = useCallback(event => {
    const { vin, dealerNumber, vra } = event.target;
    const variableArguments = {
      vin: vin.value.trim() || null,
      dealerNumber: dealerNumber.value.trim(),
      vra: vra.value || null,
    };
    refetch({
      ...variableArguments,
    });

    setSearchFieldsAction(variableArguments);
    setPageAction({});
  }, [refetch, setSearchFieldsAction, setPageAction]);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onSelectHandler = useCallback((event, data) => {
    setReturnLocation(data.value);
  }, []);

  const returnLocationOptions = !isEmpty(locations)
    && locations.map(location => ({
      text: `${location.initials} - ${location.name}`,
      key: location.id,
      value: parseInt(location.id, 10),
    }));

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumber', 'vra']}
      />
      <ReturnInvoicesList
        onSelectReturnInvoice={onSelectReturnInvoice}
        returnInvoices={canceledReturnInvoices}
      />
      <Modal
        open={isConfirmationOpen}
        closeIcon
        onClose={handleConfirmationClose}
        closeOnEscape={false}
        closeOnRootNodeClick={false}
      >
        <Modal.Header>
          Reinstate VRA -{' '}
          {isEmpty(selectedReturnInvoice) ? null : selectedReturnInvoice.id}
        </Modal.Header>
        <Modal.Content image>
          <Modal.Description>
            <Form>
              <Header size='small' color='blue' />
              <Form.Group widths='equal'>
                <Form.Field required error={!isEmpty(errors.returnLocation)}>
                  <label htmlFor='returnLocation'>Please select the Return Location</label>
                  <Select
                    id='returnLocation'
                    placeholder='Select Return Location'
                    options={returnLocationOptions}
                    value={returnLocation}
                    onChange={onSelectHandler}
                  />
                  {errors.returnLocation && (
                    <span style={{ color: 'red' }}>
                      {errors.returnLocation}
                    </span>
                  )}
                </Form.Field>
              </Form.Group>
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button color='green' onClick={handleConfirm} inverted>
            <Icon name='checkmark' /> Submit
          </Button>
          <Button color='red' onClick={handleConfirmationClose} inverted>
            <Icon name='checkmark' />
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
      {isAuditModalOpen && selectedReturnInvoice && (
        <ReturnInvoiceAudit
          isAuditModalOpen={isAuditModalOpen}
          onCloseHandler={onAuditCloseHandler}
          returnInvoice={selectedReturnInvoice}
        />
      )}
    </div>
  );
};

CanceledReturnInvoices.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  reinstateMutation: PropTypes.func.isRequired,
  data: PropTypes.shape({
    canceledReturnInvoices: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  setPage: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  searchFields: PropTypes.object.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const { data: { searchFields } } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(returnInvoicesQuery, 'canceledReturnInvoices'),
  graphql(reinstateReturnInvoiceMutation, { name: 'reinstateMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(CanceledReturnInvoices);
